import { union } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { fetchCustomAppearance } from '~/data/appearance';
import {
  fetchApplicationConfig,
  useApplicationConfig,
} from '~/data/applicationConfig';
import { getAttributes } from '~/data/attributes';
import { fetchCompanyData } from '~/data/company';
import { useCompanyData } from '~/data/company/useCompanyData';
import { fetchCostCenters, useAllCostCenters } from '~/data/costCenters';
import { fetchCustomTranslations } from '~/data/customTranslations';
import { useCustomTranslations } from '~/data/customTranslations/useCustomTranslations';
import { fetchEmployees, useAllEmployees } from '~/data/employees';
import { fetchEnums, useAllEnums } from '~/data/enums';
import { fetchLanguages, useLanguages } from '~/data/languages';
import { fetchLegalEntities, useAllLegalEntities } from '~/data/legalEntity';
import { fetchOrganizationUnits, useAllOrgUnits } from '~/data/orgunits';
import { fetchUnitPositions } from '~/data/unitPositions';
import { useAllUnitPositions } from '~/data/unitPositions/useAllUnitPositions';
import { useInitialDataFetch } from '~/hooks/initializeData/useInitialDataFetch';
import { StoreState } from '~/reducer';
import {
  appearanceStateSelector,
  authStoreStateSelector,
  companyDataStateSelector,
} from '~/selectors';
import { AppModules } from '~/services/Permissions';
import { AppDispatch } from '~/store';

export enum DispatchReduxFetch {
  ApplicationConfig = 'applicationConfig',
  CompanyData = 'companyData',
  CustomAppearance = 'customAppearance',
  AllEnums = 'allEnums',

  Languages = 'languages',
  CustomTranslations = 'customTranslations',

  AllEmployees = 'allEmployees',
  AllOrgUnits = 'allOrgUnits',
  AllCostCenters = 'allCostCenters',
  AllUnitPositions = 'allUnitPositions',
  AllLegalEntities = 'allLegalEntities',
  EmploymentDetailsAttributes = 'employmentDetailsAttributes',
}

export const coreDataDispatchReduxFetches = [
  DispatchReduxFetch.ApplicationConfig,
  DispatchReduxFetch.CompanyData,
  DispatchReduxFetch.CustomAppearance,
  DispatchReduxFetch.AllEnums,
];

export const portalMinimumDataDispatchReduxFetches = [
  ...coreDataDispatchReduxFetches,
  DispatchReduxFetch.Languages,

  DispatchReduxFetch.AllOrgUnits,
  DispatchReduxFetch.AllCostCenters,
  DispatchReduxFetch.AllUnitPositions,
  DispatchReduxFetch.AllLegalEntities,

  DispatchReduxFetch.EmploymentDetailsAttributes,
];

export const allPortalDataDispatchReduxFetches = [
  ...portalMinimumDataDispatchReduxFetches,
  DispatchReduxFetch.CustomTranslations,
  DispatchReduxFetch.AllEmployees,
];

type DataState = {
  hasData: boolean;
  isLoading: boolean;
};

type DispatchReduxFetchStates = {
  [key in DispatchReduxFetch]?: { hasData: boolean; isLoading: boolean };
};

type DispatchReduxFetchStatesSummary = {
  all: DispatchReduxFetch[];
  notInitialized: DispatchReduxFetch[];
  initializing: DispatchReduxFetch[];
  initialized: DispatchReduxFetch[];
};

type DispatchReduxFetchStatesCounts = {
  all: number;
  notInitialized: number;
  initializing: number;
  initialized: number;
};

type DispatchReduxFetchState = {
  isInitializing: boolean;
  isReady: boolean;
  percentage: string;
  counts: DispatchReduxFetchStatesCounts;
};

type PropsType = {
  required?: DispatchReduxFetch[];
  preload?: DispatchReduxFetch[];
};

type UseInitializeCommonReduxDataOutput = {
  requiredStatus: DispatchReduxFetchState;
  preloadStatus: DispatchReduxFetchState;
};

export const useInitializeCommonReduxData = (
  props: PropsType,
): UseInitializeCommonReduxDataOutput => {
  const { required, preload } = props;

  const { userDetails } = useSelector(authStoreStateSelector);
  const {
    modules: { enabledModules },
  } = useSelector(companyDataStateSelector);

  const isAuthenticated = !!userDetails;

  const { customAppearanceEnabled, fetchKeys } = useMemo(() => {
    const customAppearanceEnabled = enabledModules.includes(
      AppModules.BRANDING,
    );

    const requestedKeys = union(required, preload).filter((key) => {
      switch (key) {
        case DispatchReduxFetch.CustomAppearance:
          return customAppearanceEnabled;
        default:
          return true;
      }
    });

    const requiredKeys = requestedKeys.filter(
      (k) => !!required && required.includes(k),
    );

    const preloadKeys = requestedKeys.filter(
      (k) => !!preload && preload.includes(k),
    );
    return {
      customAppearanceEnabled,
      fetchKeys: {
        requested: requestedKeys,
        required: requiredKeys,
        preload: preloadKeys,
      },
    };
  }, [required, preload, enabledModules]);

  const dispatch: AppDispatch = useDispatch();

  const shouldDispatchFetch = useCallback(
    (condition: boolean, doFetch: () => void): void => {
      if (condition) {
        doFetch();
      }
    },
    [],
  );

  const getStates = useCallback(
    <TState extends DataState>(states: TState): DataState => {
      const { isLoading, hasData } = states;
      return { isLoading, hasData };
    },
    [],
  );

  const isFetchDisabled = useCallback(
    (fetchName: DispatchReduxFetch) => {
      return !isAuthenticated || !fetchKeys.requested.includes(fetchName);
    },
    [fetchKeys, isAuthenticated],
  );

  const applicationConfigState = useInitialDataFetch({
    dataState: useApplicationConfig(),
    onFetchData: () => dispatch(fetchApplicationConfig()),
    isDisabled: isFetchDisabled(DispatchReduxFetch.ApplicationConfig),
  });

  const companyDataState = useInitialDataFetch({
    dataState: useCompanyData(),
    onFetchData: () => dispatch(fetchCompanyData()),
    isDisabled: isFetchDisabled(DispatchReduxFetch.CompanyData),
  });

  const appearanceSelectorState = useSelector((state: StoreState) =>
    appearanceStateSelector(state),
  );
  const appearanceState = useInitialDataFetch({
    dataState:
      customAppearanceEnabled && appearanceSelectorState.failedToLoad
        ? { hasData: true, isLoading: false }
        : appearanceSelectorState,
    onFetchData: () => dispatch(fetchCustomAppearance()),
    isDisabled:
      isFetchDisabled(DispatchReduxFetch.CustomAppearance) ||
      !customAppearanceEnabled,
  });

  const enumsState = useInitialDataFetch({
    dataState: useAllEnums(),
    onFetchData: () => dispatch(fetchEnums()),
    isDisabled: isFetchDisabled(DispatchReduxFetch.AllEnums),
  });

  // Languages & Translations

  const languagesState = useInitialDataFetch({
    dataState: useLanguages(),
    onFetchData: () => dispatch(fetchLanguages()),
    isDisabled: isFetchDisabled(DispatchReduxFetch.Languages),
  });

  const customTranslationsState = useInitialDataFetch({
    dataState: useCustomTranslations(),
    onFetchData: () => dispatch(fetchCustomTranslations()),
    isDisabled: isFetchDisabled(DispatchReduxFetch.CustomTranslations),
  });

  // Employees & Units data

  const allEmployeesState = getStates(useAllEmployees());
  const shouldFetchAllEmployees =
    !allEmployeesState.hasData &&
    !allEmployeesState.isLoading &&
    isAuthenticated &&
    fetchKeys.requested.includes(DispatchReduxFetch.AllEmployees);

  const allOrgUnitsState = getStates(useAllOrgUnits());
  const shouldFetchAllOrgUnits =
    !allOrgUnitsState.hasData &&
    !allOrgUnitsState.isLoading &&
    isAuthenticated &&
    fetchKeys.requested.includes(DispatchReduxFetch.AllOrgUnits);

  const shouldFetchEmploymentDetailAttributes = fetchKeys.requested.includes(
    DispatchReduxFetch.EmploymentDetailsAttributes,
  );

  useEffect(() => {
    if (!!userDetails) {
      const { roles, employeeId } = userDetails;

      shouldDispatchFetch(shouldFetchAllEmployees, () =>
        dispatch(fetchEmployees({ myRoles: roles })),
      );
      shouldDispatchFetch(shouldFetchAllOrgUnits, () =>
        dispatch(
          fetchOrganizationUnits({
            myRoles: roles,
            myId: employeeId,
          }),
        ),
      );

      if (shouldFetchEmploymentDetailAttributes) {
        dispatch(getAttributes('EMPLOYMENT_DETAILS'));
      }
    }
  }, [
    dispatch,
    userDetails,
    shouldDispatchFetch,
    shouldFetchAllEmployees,
    shouldFetchAllOrgUnits,
    shouldFetchEmploymentDetailAttributes,
  ]);

  const allCostCentersState = useInitialDataFetch({
    dataState: useAllCostCenters(),
    onFetchData: () => dispatch(fetchCostCenters()),
    isDisabled: isFetchDisabled(DispatchReduxFetch.AllCostCenters),
  });

  const allUnitPositionsState = useInitialDataFetch({
    dataState: useAllUnitPositions(),
    onFetchData: () => dispatch(fetchUnitPositions()),
    isDisabled: isFetchDisabled(DispatchReduxFetch.AllUnitPositions),
  });

  const allLegalEntitiesState = useInitialDataFetch({
    dataState: useAllLegalEntities(),
    onFetchData: () => dispatch(fetchLegalEntities()),
    isDisabled: isFetchDisabled(DispatchReduxFetch.AllLegalEntities),
  });

  const getKeyStatusSummary = useCallback(
    (props: {
      keys: DispatchReduxFetch[];
      states: DispatchReduxFetchStates;
    }) => {
      const { keys, states } = props;

      const keyStrings = keys.map((key) => key.toString());
      const keyStates: [DispatchReduxFetch, DataState][] = Object.entries(
        states,
      )
        .filter(([k]) => keyStrings.includes(k))
        .map(([k, v]) => {
          return [k as DispatchReduxFetch, v];
        });

      const initialSummary: DispatchReduxFetchStatesSummary = {
        all: keyStates.map(([k]) => k),
        notInitialized: [],
        initializing: [],
        initialized: [],
      };

      return keyStates.reduce(
        (acc, [key, state]): DispatchReduxFetchStatesSummary => {
          const { initialized, initializing, notInitialized } = acc;
          const { hasData, isLoading } = state;

          if (hasData) {
            return {
              ...acc,
              initialized: [...initialized, key],
            };
          } else if (!hasData && isLoading) {
            return {
              ...acc,
              initializing: [...initializing, key],
            };
          } else {
            return {
              ...acc,
              notInitialized: [...notInitialized, key],
            };
          }
        },
        initialSummary,
      );
    },
    [],
  );

  const getStatusCounts = useCallback(
    (
      statusSummary: DispatchReduxFetchStatesSummary,
    ): DispatchReduxFetchStatesCounts => {
      return Object.fromEntries(
        Object.entries(statusSummary).map(([key, array]) => {
          return [key, array.length];
        }),
      ) as DispatchReduxFetchStatesCounts;
    },
    [],
  );

  const getInitializationState = useCallback(
    (counts: DispatchReduxFetchStatesCounts): DispatchReduxFetchState => {
      const { all, notInitialized, initializing, initialized } = counts;

      const percentage = `${Math.floor((initialized / all) * 100)}%`;

      return {
        isInitializing: notInitialized > 0 || initializing > 0,
        isReady: initialized === all,
        percentage,
        counts,
      };
    },
    [],
  );

  const { requiredStatus, preloadStatus } = useMemo(() => {
    const states: DispatchReduxFetchStates = {
      [DispatchReduxFetch.ApplicationConfig]: applicationConfigState,
      [DispatchReduxFetch.CompanyData]: companyDataState,
      [DispatchReduxFetch.CustomAppearance]: appearanceState,
      [DispatchReduxFetch.AllEnums]: enumsState,
      [DispatchReduxFetch.Languages]: languagesState,
      [DispatchReduxFetch.CustomTranslations]: customTranslationsState,
      [DispatchReduxFetch.AllEmployees]: allEmployeesState,
      [DispatchReduxFetch.AllOrgUnits]: allOrgUnitsState,
      [DispatchReduxFetch.AllCostCenters]: allCostCentersState,
      [DispatchReduxFetch.AllUnitPositions]: allUnitPositionsState,
      [DispatchReduxFetch.AllLegalEntities]: allLegalEntitiesState,
    };

    const getInitializationStateFromFetchKeys = (keys: DispatchReduxFetch[]) =>
      getInitializationState(
        getStatusCounts(getKeyStatusSummary({ keys, states })),
      );

    return {
      requiredStatus: getInitializationStateFromFetchKeys(fetchKeys.required),
      preloadStatus: getInitializationStateFromFetchKeys(fetchKeys.preload),
    };
  }, [
    getKeyStatusSummary,
    getStatusCounts,
    getInitializationState,
    fetchKeys,
    applicationConfigState,
    companyDataState,
    appearanceState,
    enumsState,
    languagesState,
    customTranslationsState,
    allEmployeesState,
    allOrgUnitsState,
    allCostCentersState,
    allUnitPositionsState,
    allLegalEntitiesState,
  ]);

  return {
    requiredStatus,
    preloadStatus,
  };
};
